import React from 'react';
import { RenderRequest, RenderResponse, DynamicRenderResponse } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { useDebounceEffect } from 'hooks/useDebounceEffect';
import { PreviewFrame } from './PreviewFrame';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { parseHtmlSections, SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { resolveScenarioId } from 'utils/cx-editor';
import { isWebWidgetTemplate } from 'constants/web-widget-mapping';

type Props = { 
  request: RenderRequest | null; 
  rightRequest?: RenderRequest | null;
  previewMode?: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  projectId?: string | null;
  elementKey?: string; // Current selected element/section
  onSectionsParsed?: (sections: Array<{ elementKey: string; displayName: string }>) => void; // Callback to update element dropdown
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  onHtmlChange?: (html: string) => void; // Callback to expose rendered HTML to parent
  variant?: { scenario?: string } | null; // Variant with scenario for Web widgets
};

// Module-level in-flight tracking to prevent duplicate calls across component instances and StrictMode re-renders
const inFlightRenders = new Set<string>();

export const PreviewArea: React.FC<Props> = ({ 
  request, 
  rightRequest,
  previewMode = 'desktop',
  channel = 'Email',
  isFullTemplateSelected = false,
  projectId,
  elementKey = 'Full Template',
  onSectionsParsed,
  onRenderComplete,
  onHtmlChange,
  variant,
}) => {
  const { rc } = useGlobalConfig();
  const selectedProjectId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  const effectiveProjectId = projectId || selectedProjectId;
  
  // Extract fuel_type from projectAttributes (similar to InspectorPanel)
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);
  
  const [fullHtml, setFullHtml] = React.useState<string>(''); // Store full rendered HTML
  const [html, setHtml] = React.useState<string>(''); // Filtered HTML based on elementKey
  const [loading, setLoading] = React.useState(false);
  const [subject, setSubject] = React.useState<string>('');
  const [htmlB, setHtmlB] = React.useState<string>('');
  const [loadingB, setLoadingB] = React.useState(false);
  const [subjectB, setSubjectB] = React.useState<string>('');
  
  // Helper function to get sectionNames array from elementKey
  // Returns empty array for Full Template, or array with technical section name
  // This function works without HTML by using SECTION_ID_TO_ELEMENT_KEY_MAP for reverse lookup
  const getSectionNames = React.useCallback((elementKey: string): string[] => {
    if (elementKey === 'Full Template') {
      return [];
    }
    
    // Check if elementKey is already a technical section ID (exists as key in SECTION_ID_TO_ELEMENT_KEY_MAP)
    // This handles cases where EditorToolbar passes technical section IDs directly (e.g., "MONTHLY_SUMMARY_V2")
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return [elementKey];
    }
    
    // Reverse lookup: Find all technical section IDs that map to this display name
    // Prefer higher version numbers (e.g., V4 > V3 > V2)
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === elementKey) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      // Sort by version number (higher first) and return the first one
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; // Higher version first
      });
      return [matchingTechnicalIds[0]];
    }
    
    // Final fallback: if elementKey might be a technical section ID that's not in mapping,
    // or we can't determine the technical section ID, return empty array (backend will handle full template)
    // This is safe because backend can handle empty array for full template
    return [];
  }, []);
  
  // Client-side filtering removed - API will handle filtering based on sectionNames

  // Extract primitive values from request to use as stable dependencies
  const templateKey = request?.templateKey || null;
  const scenario = request?.variant?.scenario || null;
  const refreshKey = request?.refreshKey ?? null;

  const isWebWidget = channel === 'Web' && isWebWidgetTemplate(templateKey);
  
  // API call when template, scenario, refreshKey, or elementKey changes
  // elementKey changes trigger new API call with updated sectionNames
  useDebounceEffect(() => {
    // For Web widget templates, skip CX render API – widgets SDK will handle rendering
    if (isWebWidget) {
      setFullHtml('');
      setHtml('');
      setSubject('');
      return;
    }

    // Check and mark as in-flight SYNCHRONOUSLY (before async) to prevent race conditions
    if (!request || !effectiveProjectId || !templateKey || !scenario) {
      setFullHtml(''); 
      setHtml('');
      setSubject('');
      return;
    }
    
    // Create a unique key for this request (include elementKey and refreshKey to force refresh)
    // Different sections need different cache keys since API response differs by sectionNames
    const requestKey = `${templateKey}-${scenario}-${effectiveProjectId}-${elementKey}-${refreshKey ?? 0}`;
    
    // Prevent duplicate calls - check SYNCHRONOUSLY before async function
    // This prevents race conditions when StrictMode causes double renders
    if (inFlightRenders.has(requestKey)) {
      console.log('[PreviewArea] Request already in flight, skipping duplicate:', requestKey, {
        inFlightSet: Array.from(inFlightRenders),
        templateKey,
        scenario
      });
      return;
    }
    
    // Mark as in-flight SYNCHRONOUSLY (module-level, persists across re-renders)
    inFlightRenders.add(requestKey);
    console.log('[PreviewArea] Marked request as in-flight:', requestKey);
    
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          scenario || 'default',
          templateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          fuelType,
          request.variant?.locale || 'en_US',
          (pilot_id, fuel_type, locale) => api.getDataScenarios(pilot_id, fuel_type, locale)
        );
        
        // Note: resolvedScenarioId is the actual scenario ID (e.g., "MS_1", "MS_2"), not "default"
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames? }
        // The scenarioId should be resolved and contain template information
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        console.log('[PreviewArea] Calling renderDynamic API', {
          projectId: effectiveProjectId,
          originalScenario: scenario,
          resolvedScenarioId,
          templateKey: templateKey,
          elementKey,
          sectionNames: sectionNamesForApi,
          requestKey,
          apiUrl: process.env.REACT_APP_TOOL_API_URL
        });
        
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          sectionNames: sectionNamesForApi
        });
        console.log('[PreviewArea] API response received', { 
          hasHtml: !!resp.html,
          htmlLength: resp.html?.length,
          requestKey
        });
        if (active && inFlightRenders.has(requestKey)) {
          // Clear in-flight flag
          inFlightRenders.delete(requestKey);
          
          const renderedHtml = resp.html || '';
          // API returns filtered HTML based on sectionNames, so use it directly (no client-side filtering)
          setFullHtml(renderedHtml); // Store full HTML (may be filtered by backend)
          setHtml(renderedHtml); // Use API response directly - backend handles filtering
          setSubject(resp.subject || ''); // renderDynamic returns subject
          
          // Expose HTML to parent (for InspectorPanel to get technical section IDs)
          // Note: For section-specific renders, HTML may be filtered, but we still expose it
          if (onHtmlChange && renderedHtml) {
            onHtmlChange(renderedHtml);
          }
          
          // Parse sections from HTML and notify parent
          // Only parse when rendering Full Template to get all available sections
          if (renderedHtml && onSectionsParsed && elementKey === 'Full Template') {
            const { elementKeys } = parseHtmlSections(renderedHtml);
            // Create elements array with "Full Template" as first option
            const sections = [
              { elementKey: 'Full Template', displayName: 'Full Template' },
              ...elementKeys.map(key => ({ elementKey: key, displayName: key }))
            ];
            onSectionsParsed(sections);
          }
          
          // Notify parent that render is complete - trigger template/scenario fetch
          if (onRenderComplete && templateKey) {
            onRenderComplete(templateKey);
          }
        }
      } catch (err) {
        console.error('[PreviewArea] API call failed', err);
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          const errorHtml = '<div style="padding:12px;color:crimson">Failed to render preview</div>';
          setFullHtml(errorHtml);
          setHtml(errorHtml);
          setSubject('');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => { 
      active = false;
      // Don't clear inFlightRef here - let the request complete naturally
    };
  }, 400, [templateKey, scenario, effectiveProjectId, elementKey, refreshKey]); // Include elementKey to trigger API call when section changes

  // Extract primitive values from rightRequest for stable dependencies
  const rightTemplateKey = rightRequest?.templateKey || null;
  const rightScenario = rightRequest?.variant?.scenario || null;
  const rightLocale = rightRequest?.variant?.locale || null;
  
  useDebounceEffect(() => {
    let active = true;
    (async () => {
      // For Web widget templates, skip CX render API – widgets SDK will handle rendering
      if (channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)) {
        setHtmlB('');
        setSubjectB('');
        return;
      }

      if (!rightRequest || !effectiveProjectId || !rightTemplateKey || !rightScenario) { 
        setHtmlB(''); 
        setSubjectB('');
        return; 
      }
      
      // Create a unique key for this request (include elementKey since API response differs by sectionNames)
      const sectionNamesForApi = getSectionNames(elementKey);
      const requestKey = `${rightTemplateKey}-${rightScenario}-${effectiveProjectId}-${elementKey}-right`;
      
      // Prevent duplicate calls
      if (inFlightRenders.has(requestKey)) {
        console.log('[PreviewArea] Right request already in flight, skipping duplicate:', requestKey);
        return;
      }
      
      // Mark as in-flight
      inFlightRenders.add(requestKey);
      
      setLoadingB(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          rightScenario || 'default',
          rightTemplateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          fuelType,
          rightLocale || 'en_US',
          (pilot_id, fuel_type, locale) => api.getDataScenarios(pilot_id, fuel_type, locale)
        );
        
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames? }
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          sectionNames: sectionNamesForApi
        });
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          setHtmlB(resp.html || '');
          setSubjectB(resp.subject || ''); // renderDynamic returns subject
        }
      } catch (err) {
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          setHtmlB('<div style="padding:12px;color:crimson">Failed to render preview</div>');
          setSubjectB('');
        }
      } finally {
        if (active) setLoadingB(false);
      }
    })();
    return () => { active = false; };
  }, 400, [rightTemplateKey, rightScenario, effectiveProjectId]);
  if (rightRequest) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          overflow: 'hidden'
        }}
      >
        <PreviewFrame
          variantName="A"
          variant={variant || request?.variant || null}
          renderRequest={request}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={html}
          renderedSubject={subject}
          loading={loading}
          isWebWidgetTemplate={isWebWidget}
        />
        <Box
          sx={{
            width: '1px',
            bgcolor: 'divider'
          }}
        />
        <PreviewFrame
          variantName="B"
          variant={variant || rightRequest?.variant || null}
          renderRequest={rightRequest}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={htmlB}
          renderedSubject={subjectB}
          loading={loadingB}
          isWebWidgetTemplate={
            channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)
          }
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 2,
        p: 2,
        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        overflow: 'auto'
      }}
    >
      <PreviewFrame
        variant={variant || request?.variant || null}
        renderRequest={request}
        previewMode={previewMode}
        channel={channel}
        isFullTemplateSelected={isFullTemplateSelected}
        renderedHtml={html}
        renderedSubject={subject}
        loading={loading}
        isWebWidgetTemplate={isWebWidget}
      />
    </Box>
  );
};
